import { useEffect, useState } from "react";
import { getAudio } from "@/audio";

const FADE_IN = 40;
const LEVEL = 0.34;
const BAR = 4;
const DRONE = [36.71, 55, 73.42];
const CHORDS = [
  [146.83, 174.61, 220, 233.08],
  [116.54, 146.83, 174.61, 207.65],
  [130.81, 155.56, 196, 207.65],
  [110, 130.81, 164.81, 174.61],
];
const CURVE = Float32Array.from({ length: 1024 }, (_, i) => Math.tanh(((i / 512) - 1) * 1.3));

const noise = (ctx, secs) => {
  const b = ctx.createBuffer(1, ctx.sampleRate * secs, ctx.sampleRate);
  const d = b.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return b;
};

const space = (ctx, secs = 5) => {
  const len = ctx.sampleRate * secs;
  const b = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let c = 0; c < 2; c++) {
    const d = b.getChannelData(c);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3.2);
  }
  const v = ctx.createConvolver();
  v.buffer = b;
  return v;
};

const lfo = (ctx, hz, amt, target, at) => {
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.frequency.value = hz;
  g.gain.value = amt;
  o.connect(g).connect(target);
  o.start(at);
  return o;
};

const thump = (ctx, dest, t) => {
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.frequency.setValueAtTime(70, t);
  o.frequency.exponentialRampToValueAtTime(38, t + 0.25);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(0.32, t + 0.03);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
  o.connect(g).connect(dest);
  o.start(t);
  o.stop(t + 1);
};

const dust = (ctx, dest, buf, t) => {
  const s = ctx.createBufferSource();
  s.buffer = buf;
  const f = ctx.createBiquadFilter();
  f.type = "bandpass";
  f.frequency.value = 2200 + Math.random() * 1500;
  f.Q.value = 3;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.06, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
  s.connect(f).connect(g).connect(dest);
  s.start(t, Math.random());
  s.stop(t + 0.15);
};

const pad = (ctx, dest, wobble, freqs, t, dur) => {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.06, t + dur * 0.45);
  g.gain.linearRampToValueAtTime(0, t + dur);
  g.connect(dest);
  freqs.forEach((f) => [-13, 0, 11].forEach((det, i) => {
    const o = ctx.createOscillator();
    o.type = i === 1 ? "triangle" : "sawtooth";
    o.frequency.value = f;
    o.detune.value = det;
    wobble.connect(o.detune);
    o.connect(g);
    o.start(t);
    o.stop(t + dur + 0.1);
  }));
};

const swell = (ctx, dest, buf, t, dur) => {
  const s = ctx.createBufferSource();
  s.buffer = buf;
  s.loop = true;
  const f = ctx.createBiquadFilter();
  f.type = "bandpass";
  f.Q.value = 6;
  f.frequency.setValueAtTime(260 + Math.random() * 200, t);
  f.frequency.linearRampToValueAtTime(600 + Math.random() * 500, t + dur);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.5, t + dur * 0.85);
  g.gain.linearRampToValueAtTime(0, t + dur);
  s.connect(f).connect(g).connect(dest);
  s.start(t);
  s.stop(t + dur + 0.1);
};

const start = () => {
  const { ctx, out } = getAudio();
  const now = ctx.currentTime;
  const bus = ctx.createGain();
  bus.gain.setValueAtTime(0, now);
  bus.gain.linearRampToValueAtTime(LEVEL, now + FADE_IN * (window.__ambientScale || 1));
  const tape = ctx.createWaveShaper();
  tape.curve = CURVE;
  const dark = ctx.createBiquadFilter();
  dark.type = "lowpass";
  dark.frequency.value = 1800;
  tape.connect(dark).connect(bus).connect(out);
  const verb = space(ctx);
  const wet = ctx.createGain();
  wet.gain.value = 0.85;
  verb.connect(wet).connect(tape);
  const dry = ctx.createGain();
  dry.gain.value = 0.6;
  dry.connect(tape);
  const send = ctx.createGain();
  send.connect(dry);
  send.connect(verb);
  const wobble = ctx.createGain();
  const nodes = [lfo(ctx, 0.11, 7, wobble, now)];
  const droneF = ctx.createBiquadFilter();
  droneF.type = "lowpass";
  droneF.frequency.value = 260;
  droneF.Q.value = 5;
  nodes.push(lfo(ctx, 0.03, 160, droneF.frequency, now));
  const droneG = ctx.createGain();
  droneG.gain.value = 0.22;
  droneF.connect(droneG).connect(send);
  DRONE.forEach((f, i) => [-6, 6].forEach((det) => {
    const o = ctx.createOscillator();
    o.type = i === 0 ? "sawtooth" : "triangle";
    o.frequency.value = f;
    o.detune.value = det;
    wobble.connect(o.detune);
    o.connect(droneF);
    o.start(now);
    nodes.push(o);
  }));
  const padF = ctx.createBiquadFilter();
  padF.type = "lowpass";
  padF.frequency.value = 650;
  nodes.push(lfo(ctx, 0.02, 250, padF.frequency, now));
  padF.connect(send);
  const wind = ctx.createBufferSource();
  wind.buffer = noise(ctx, 4);
  wind.loop = true;
  const windF = ctx.createBiquadFilter();
  windF.type = "bandpass";
  windF.frequency.value = 500;
  windF.Q.value = 1.4;
  nodes.push(lfo(ctx, 0.017, 300, windF.frequency, now));
  const windG = ctx.createGain();
  windG.gain.value = 0.05;
  wind.connect(windF).connect(windG).connect(send);
  wind.start(now);
  nodes.push(wind);
  const buf = noise(ctx, 2);
  const pulse = ctx.createGain();
  pulse.gain.value = 0.5;
  pulse.connect(send);
  let bar = 0;
  let next = now + 0.5;
  const loop = () => {
    while (next < ctx.currentTime + 0.5) {
      if (bar % 4 === 0) pad(ctx, padF, wobble, CHORDS[(bar / 4) % CHORDS.length], next, BAR * 4.5);
      if (bar % 2 === 0) thump(ctx, pulse, next);
      if (bar % 4 === 3 && Math.random() < 0.6) thump(ctx, pulse, next + BAR * 0.5);
      if (Math.random() < 0.35) dust(ctx, pulse, buf, next + Math.random() * BAR);
      if (bar % 8 === 6) swell(ctx, send, buf, next, BAR * 2);
      next += BAR;
      bar++;
    }
  };
  const id = setInterval(loop, 200);
  loop();
  window.__ambientOn = true;
  window.__ambientLevel = LEVEL;
  window.__ambientStyle = "dark";
  return () => {
    clearInterval(id);
    window.__ambientOn = false;
    bus.gain.cancelScheduledValues(ctx.currentTime);
    bus.gain.setTargetAtTime(0, ctx.currentTime, 0.3);
    setTimeout(() => { nodes.forEach((x) => { try { x.stop(); } catch { /* stopped */ } }); bus.disconnect(); }, 2000);
  };
};

export const useAmbientBeat = (enabled, delayMs = 60000) => {
  const [due, setDue] = useState(false);
  useEffect(() => {
    const ms = window.__ambientDelay ?? delayMs;
    window.__ambientStartsAt = Date.now() + ms;
    const t = setTimeout(() => setDue(true), ms);
    return () => clearTimeout(t);
  }, [delayMs]);
  useEffect(() => {
    if (!due || !enabled) return;
    let stop;
    const { ctx } = getAudio();
    const begin = () => {
      if (ctx.state !== "running") {
        ctx.addEventListener("statechange", begin, { once: true });
        return;
      }
      stop = start();
    };
    begin();
    return () => {
      ctx.removeEventListener("statechange", begin);
      stop?.();
    };
  }, [due, enabled]);
};
